import React, { FC, useEffect } from 'react'
import { SubDescription, SubHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { DaysOfWeekPicker, SelectBox } from 'components/Form'
import { useForwardLastOrderTime, useTimeOptions } from 'hooks/businessHour'
import isAfter from 'date-fns/isAfter'
import { parseTimeToDate } from 'lib/helper'
import { useLastPickupTimes, useTimesWithStartTime } from 'hooks/store'
import { DayOfWeekType } from 'lib/daysOfWeek'

type Props = {
  orderStartTime: string
  openTime: string
  lastOrderTime: string
  lastPickupTime: string
  selectOrderStartTime: (time: string) => void
  selectOpenTime: (time: string) => void
  selectLastOrderTime: (time: string) => void
  selectLastPickupTime: (time: string) => void
  businessDays: DayOfWeekType[]
  defaultDayOfWeek: DayOfWeekType | ''
  selectDefaultDayOfWeek: (dayOfWeek: DayOfWeekType) => void
}

const OpenStoreCalendarForm: FC<Props> = (props) => {
  const {
    orderStartTime,
    openTime,
    lastOrderTime,
    lastPickupTime,
    selectOrderStartTime,
    selectOpenTime,
    selectLastOrderTime,
    selectLastPickupTime,
    businessDays,
    defaultDayOfWeek,
    selectDefaultDayOfWeek,
  } = props

  // オープン時刻+15分が、ラストオーダー時刻以降であればラストオーダー時刻を更新する
  useForwardLastOrderTime(orderStartTime, lastOrderTime, (time: string) => selectLastOrderTime(time))

  // ラストオーダー時刻が最終受取時刻より遅い時刻であれば最終受取時刻を更新する
  useEffect(() => {
    if (isAfter(parseTimeToDate(lastOrderTime), parseTimeToDate(lastPickupTime))) {
      selectLastPickupTime(lastOrderTime)
    }
  }, [lastOrderTime])

  const openTimes = useTimeOptions()
  const orderStartTimes = useTimeOptions()
  // オープン時刻 < ラストオーダー時刻となる時間を選べるよう時間のオプションを生成
  const lastOrderTimes = useTimesWithStartTime(orderStartTime)
  // ラストオーダー時刻 <= 最終受渡時刻となる時間を選べるよう時間のオプションを生成
  const lastPickupTimes = useLastPickupTimes(lastOrderTime, orderStartTime)

  return (
    <div>
      {/* オーダー開始時刻 */}
      <SubHeadline headline={'オーダー開始時刻'} />
      <Spacer size={8} />
      <SubDescription description={'当日取り置き注文を受け付ける時刻を設定してください'} />
      <Spacer size={8} />
      <SelectBox options={orderStartTimes} select={selectOrderStartTime} value={orderStartTime} />
      <Spacer size={16} />
      {/* 店舗オープン時刻 */}
      <SubHeadline headline={'店舗オープン時刻'} />
      <Spacer size={8} />
      <SubDescription description={'お店が開店し商品を受け渡しできる時刻を設定してください'} />
      <Spacer size={8} />
      <SelectBox options={openTimes} select={selectOpenTime} value={openTime} />
      <Spacer size={16} />
      {/* ラストオーダー時刻 */}
      <SubHeadline headline={'ラストオーダー時刻'} />
      <Spacer size={8} />
      <SubDescription description={'当日取り置き注文を締め切る時刻を設定してください'} />
      <Spacer size={8} />
      <SelectBox options={lastOrderTimes} select={selectLastOrderTime} value={lastOrderTime} />
      <Spacer size={16} />
      {/* 最終受渡時刻 */}
      <SubHeadline headline={'最終受渡時刻'} />
      <Spacer size={8} />
      <SubDescription description={'商品の受け渡しができる最終時刻を設定してください'} />
      <Spacer size={8} />
      <SelectBox options={lastPickupTimes} select={selectLastPickupTime} value={lastPickupTime} />
      {/* 商品設定の引き継ぎ */}
      <Spacer size={16} />
      <SubHeadline headline={'商品設定の引き継ぎ'} />
      <Spacer size={8} />
      <SubDescription
        description={
          '登録されている商品の受渡し解禁時刻や在庫数の設定を、営業日から引き継ぎます。設定を引き継ぎしたい曜日を選択してください。\n\n当日取り置きの都度配信や、事前予約・お取り寄せ商品に関しては、追加で在庫登録や販売日の設定が必要です。'
        }
      />
      <Spacer size={8} />
      <DaysOfWeekPicker
        daysOfWeek={defaultDayOfWeek !== '' ? [defaultDayOfWeek] : []}
        selectDaysOfWeek={selectDefaultDayOfWeek}
        wrap={false}
        selectableDaysOfWeek={businessDays}
      />
    </div>
  )
}

export default OpenStoreCalendarForm
